import { useState } from "react";
import ComplianceDialog from "./ComplianceDialog";
import { Folder, FolderOpen, ArrowsClockwise, FilePlus, CaretRight, DownloadSimple, ListNumbers, CheckCircle, Circle } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const SHEET_FORMATS = [
  ["pdf", "PDF — issue / print"],
  ["dxf", "DXF — AutoCAD / CAM"],
  ["svg", "SVG — vector / web"],
  ["png", "PNG — raster preview"],
];

function DownloadMenu({ drawing, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`download-${testid}`} title="Download this sheet"
        onClick={() => setOpen((o) => !o)}
        className="text-[var(--c-muted)] hover:text-[var(--c-accent)] opacity-0 group-hover:opacity-100">
        <DownloadSimple size={13} />
      </button>
      {open && (
        <div data-testid={`download-menu-${testid}`}
          className="absolute right-0 top-5 z-50 w-52 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-xl p-1.5">
          <div className="px-2 py-1 text-[9px] font-mono uppercase tracking-wider text-[var(--c-muted)]">
            {drawing.drawing_no} — format
          </div>
          {SHEET_FORMATS.map(([fmt, label]) => (
            <button key={fmt} data-testid={`download-${testid}-${fmt}`}
              onClick={() => { setOpen(false); window.open(`${BASE}/api/drawings/${drawing.id}/${fmt}`, "_blank"); }}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-sm text-[11px] text-[var(--c-text)] hover:bg-[var(--c-accent)]/10">
              <span className="font-mono uppercase">{fmt}</span>
              <span className="text-[10px] text-[var(--c-muted)] truncate">{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


const GROUPS = [
  ["Core", ["core_assembly"]],
  ["Windings", ["winding_hv", "winding_lv"]],
  ["Assembly", ["coil_assembly"]],
  ["Tank", ["tank"]],
  ["General Arrangement", ["ga"]],
  ["Electrical", ["connection_diagram", "wiring_diagram"]],
  ["Other", ["nameplate"]],
];

export const STATUS_COLORS = {
  draft: "var(--c-muted)", checked: "var(--c-accent)", approved: "var(--c-green)", released: "var(--c-amber)", superseded: "#555",
};

export default function DrawingTree({ meta, drawings, selId, projectId, onSelect, onGenerate,
                                     onGenerateAll, onRefresh, busy }) {
  const [collapsed, setCollapsed] = useState(new Set());
  const [seqOpen, setSeqOpen] = useState(true);
  const [compOpen, setCompOpen] = useState(false);
  const metaFor = (typeId) => meta.find((m) => m.id === typeId);
  // universal grouping: any pack drawing type not in the PT-era static groups
  // gets its own group derived from pack meta — zero frontend changes per industry
  const covered = new Set(GROUPS.flatMap(([, types]) => types));
  // annotation continuation sheets belong with their parent drawing, never in their own folder
  const isNotes = (id) => String(id).endsWith("__notes");
  const dynamicGroups = GROUPS.map(([g, types]) => [g, types.flatMap((t) => [t, `${t}__notes`])]);
  dynamicGroups.push(...meta.filter((m) => !covered.has(m.id) && !isNotes(m.id))
    .map((m) => [m.name, [m.id, `${m.id}__notes`]]));
  const revsFor = (typeId) =>
    drawings.filter((d) => d.type === typeId && d.status !== "superseded")
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));

  const sequence = meta.filter((m) => !String(m.id).endsWith("__notes"));
  // counted exactly the way the tree below lists sheets, so the two can never disagree
  const done = new Set(sequence.filter((m) => revsFor(m.id).length).map((m) => m.id));
  const nextUp = sequence.find((m) => !done.has(m.id));
  // sheet compliance travels with each generated drawing — the set is compliant only when
  // every identified sheet is, so the roll-up is counted here the same way
  const compFor = (typeId) => {
    const revs = revsFor(typeId);
    return revs.length ? revs[revs.length - 1].sheet_compliance : null;
  };
  const comps = sequence.map((m) => compFor(m.id)).filter(Boolean);
  const compliant = comps.filter((c) => c.status === "pass").length;
  const failing = comps.filter((c) => c.status === "fail").length;
  const setTone = !comps.length ? "var(--c-muted)"
    : failing ? "var(--c-red, #dc2626)" : compliant === comps.length ? "var(--c-green)" : "var(--c-amber)";
  const compSheets = sequence.filter((m) => compFor(m.id)).map((m) => {
    const revs = revsFor(m.id);
    const last = revs[revs.length - 1];
    return { ...compFor(m.id), type: m.id, name: m.name, scale: last?.scale,
             revision: last?.rev_label, drawing_no: last?.drawing_no };
  });
  const compMissing = sequence.filter((m) => !revsFor(m.id).length)
    .map((m) => ({ type: m.id, name: m.name }));

  return (
    <div className="flex flex-col h-full" data-testid="drawing-tree">
      <div className="border-b border-[var(--c-border)]" data-testid="generation-sequence">
        <button onClick={() => setSeqOpen((v) => !v)} data-testid="sequence-toggle"
          className="w-full flex items-center gap-1.5 px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--c-muted)] hover:text-[var(--c-text)]">
          <CaretRight size={9} className={`shrink-0 transition-transform duration-100 ${seqOpen ? "rotate-90" : ""}`} />
          <ListNumbers size={12} /> Generation order
          <span className="ml-auto text-[9px]">{done.size}/{sequence.length}</span>
        </button>
        {seqOpen && (
          <div className="px-2 pb-2">
            <div className="px-1 pb-2 text-[10px] leading-snug text-[var(--c-muted)]">
              Generate in this order — each sheet reuses the geometry settled by the one before it.
              {nextUp ? <> Next: <b className="text-[var(--c-text)]">{nextUp.name}</b>.</>
                : " All sheets generated."}
            </div>
            {sequence.map((m, i) => {
              const isDone = done.has(m.id);
              const isNext = nextUp?.id === m.id;
              const waiting = !isDone && !isNext;
              return (
                <div key={m.id} data-testid={`sequence-row-${m.id}`}
                  className={`flex items-center gap-2 px-1.5 py-1 rounded-sm ${isNext ? "bg-[var(--c-accent)]/10" : ""}`}>
                  <span className="font-mono text-[9px] w-3 text-right text-[var(--c-muted)]">{i + 1}</span>
                  {isDone ? <CheckCircle size={12} className="text-[var(--c-green)] shrink-0" />
                    : <Circle size={12} className={`shrink-0 ${isNext ? "text-[var(--c-accent)]" : "text-[var(--c-muted)]/50"}`} />}
                  <span className={`flex-1 text-[11px] truncate ${waiting ? "text-[var(--c-muted)]" : "text-[var(--c-text)]"}`}>
                    {m.name}
                  </span>
                  <button data-testid={`sequence-generate-${m.id}`} onClick={() => onGenerate(m.id)}
                    disabled={!!busy}
                    title={waiting ? "Prerequisite sheets above are not generated yet — you can still build this one"
                      : isDone ? "Regenerate this sheet" : "Generate this sheet now"}
                    className={`shrink-0 h-5 px-2 rounded-sm text-[10px] transition-colors duration-100 disabled:opacity-40 ${
                      isNext ? "bg-[var(--c-accent)] text-white hover:bg-[var(--c-accent-hover)]"
                        : "border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
                    {busy === m.id ? "…" : isDone ? "Redo" : "Generate"}
                  </button>
                </div>
              );
            })}
            <button data-testid="generate-all-btn" onClick={onGenerateAll} disabled={!!busy}
              className="mt-2 w-full h-7 text-[11px] rounded-sm border border-[var(--c-border)] text-[var(--c-text)] hover:border-[var(--c-accent)] disabled:opacity-50 flex items-center justify-center gap-1.5">
              <ArrowsClockwise size={12} className={busy === "all" ? "animate-spin" : ""} />
              {busy === "all" ? "Generating the full set…" : "Generate All (runs this sequence)"}
            </button>
            <div className="px-1 pt-1.5 text-[9px] leading-snug text-[var(--c-muted)]">
              Optional. Generate All runs exactly the sequence above; one at a time gives you
              a check after every sheet.
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {dynamicGroups.map(([group, types]) => {
          const present = types.filter((t) => metaFor(t));
          if (!present.length) return null;
          return (
            <div key={group} className="mb-1">
              <button data-testid={`tree-group-${group.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCollapsed((s) => { const q = new Set(s); if (q.has(group)) q.delete(group); else q.add(group); return q; })}
                className="w-full flex items-center gap-1.5 px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-[var(--c-muted)] hover:text-[var(--c-text)] transition-colors duration-100">
                <CaretRight size={9} className={`shrink-0 transition-transform duration-100 ${collapsed.has(group) ? "" : "rotate-90"}`} />
                {collapsed.has(group) ? <Folder size={11} /> : <FolderOpen size={11} />} {group}
                <span className="ml-auto text-[9px]">{present.length}</span>
              </button>
              {!collapsed.has(group) && present.map((t) => {
                const mt = metaFor(t);
                const revs = revsFor(t);
                if (!revs.length) {
                  return (
                    <div key={t} className="flex items-center gap-1.5 pl-7 pr-2 py-1.5 opacity-50" data-testid={`tree-row-${t}`}>
                      <span className="flex-1 text-[11px] text-[var(--c-muted)] truncate">{mt.name}</span>
                      <span className="font-mono text-[9px] text-[var(--c-muted)]">Not generated</span>
                      <button data-testid={`generate-one-${t}`} onClick={() => onGenerate(t)} disabled={!!busy}
                        className="text-[var(--c-muted)] hover:text-[var(--c-accent)] disabled:opacity-40" title="Generate">
                        <FilePlus size={12} className={busy === t ? "animate-spin" : ""} />
                      </button>
                    </div>
                  );
                }
                return revs.map((d) => (
                  <div key={d.id}
                    className={`flex items-center gap-1.5 pl-7 pr-2 py-1.5 cursor-pointer group ${
                      selId === d.id ? "bg-[var(--c-accent)]/15" : "hover:bg-[var(--c-surface-2)]"}`}
                    onClick={() => onSelect(d)} data-testid={`tree-row-${t}-${d.rev_label || d.revision}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-[var(--c-text)] truncate flex items-center gap-1.5">
                        {mt.name}
                        {(d.status === "approved" || d.status === "released") && <span title="Locked">🔒</span>}
                      </div>
                      <div className="font-mono text-[9px] text-[var(--c-muted)]">Rev {d.rev_label || d.revision}</div>
                    </div>
                    <span className="font-mono text-[9px] uppercase shrink-0"
                      style={{ color: STATUS_COLORS[d.status] || "var(--c-muted)" }}
                      data-testid={`tree-status-${t}`}>{d.status}</span>
                    <DownloadMenu drawing={d} testid={`${t}-${d.rev_label || d.revision}`} />
                    <button data-testid={`generate-one-${t}`}
                      onClick={(e) => { e.stopPropagation(); onGenerate(t); }} disabled={!!busy}
                      className="text-[var(--c-muted)] hover:text-[var(--c-accent)] disabled:opacity-40 opacity-0 group-hover:opacity-100 shrink-0"
                      title="Regenerate (overwrites current draft)">
                      <ArrowsClockwise size={12} className={busy === t ? "animate-spin" : ""} />
                    </button>
                  </div>
                ));
              })}
            </div>
          );
        })}
        {!meta.length && (
          <div className="px-3 py-6 text-[11px] text-[var(--c-muted)] text-center">No drawing generators for this industry yet.</div>
        )}
      </div>
      {compOpen && (
        <ComplianceDialog projectId={projectId} sheets={compSheets} notGenerated={compMissing}
          onClose={() => setCompOpen(false)} onDone={onRefresh} />
      )}
    </div>
  );
}
